import { useState, useEffect } from "react";
import { Search, MessageSquare, Edit, FolderOpen, Wrench, User, Trash2, Edit3, Check, X } from "lucide-react";
import { chatService } from "../../services/chatService";

interface ChatSidebarProps {
  collapsed?: boolean;
  onToggle: () => void;
  activeSessionId?: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onNavigateToChat?: () => void;
  onNavigateToSettings: () => void;
  onNavigateToDocuments?: () => void;
  onNavigateToTools?: () => void;
  onNavigateToAccount?: () => void;
  onSearchChats?: (query: string) => void;
  onDeleteSession?: (sessionId: string) => void;
  onRenameSession?: (sessionId: string, newTitle: string) => void;
  currentView?: 'chat' | 'documents' | 'tools' | 'account';
  refreshTrigger?: number;
}

interface ChatSession {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt?: Date;
  lastMessage?: {
    content: string;
    timestamp: Date;
  };
}

export function ChatSidebar({ 
  collapsed = false, 
  onToggle, 
  activeSessionId,
  onSessionSelect,
  onNewChat,
  onNavigateToChat = () => {},
  onNavigateToSettings,
  onNavigateToDocuments = () => {},
  onNavigateToTools = () => {},
  onNavigateToAccount = () => {},
  onSearchChats = () => {},
  onDeleteSession = () => {},
  onRenameSession = () => {},
  currentView = 'chat',
  refreshTrigger = 0
}: ChatSidebarProps) {
  const [chatSessions, setChatSessions] = useState<import("../../services/chatService").ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const handleNewChat = async () => {
    try {
      // Navigate to chat view first if not already there
      if (currentView !== 'chat') {
        onNavigateToChat();
      }
      
      await onNewChat();
      // Refresh the sessions list
      await loadChatSessions();
    } catch (error) {
      console.error('Failed to create new chat:', error);
    }
  };

  const sidebarItems = [
    { 
      icon: Edit, 
      label: "Chat", 
      active: currentView === 'chat', 
      onClick: onNavigateToChat // Navigate to chat (creates session if needed)
    },
    { 
      icon: FolderOpen, 
      label: "Documents", 
      active: currentView === 'documents', 
      onClick: onNavigateToDocuments 
    },
    { 
      icon: Wrench, 
      label: "Tools", 
      active: currentView === 'tools', 
      onClick: onNavigateToTools 
    },
  ];

  // Load chat sessions from backend
  useEffect(() => {
    loadChatSessions();
  }, []);

  // Refresh sessions when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadChatSessions();
    }
  }, [refreshTrigger]);

  // Filter sessions based on search query
  const activeId = activeSessionId || null;
  const hasActiveInList = activeId ? chatSessions.some(s => s.id === activeId) : false;
  const sessionsWithActive = (!activeId || hasActiveInList) ? chatSessions : [
    {
      id: activeId,
      title: 'New Chat',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_message_at: new Date().toISOString(),
    },
    ...chatSessions,
  ];

  const filteredSessions = sessionsWithActive.filter(session => 
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const loadChatSessions = async () => {
    try {
      setIsLoading(true);
      const sessions = await chatService.getChatSessions();
      setChatSessions(sessions || []);
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
      setChatSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onSearchChats(query);
  };

  const handleDeleteSession = async (sessionId: string, sessionTitle: string) => {
    if (window.confirm(`Are you sure you want to delete "${sessionTitle}"? This action cannot be undone.`)) {
      try {
        await chatService.deleteChatSession(sessionId);
        // Remove from local state
        setChatSessions(prev => prev.filter(s => s.id !== sessionId));
        // Call parent handler
        onDeleteSession(sessionId);
      } catch (error) {
        console.error('Error deleting session:', error);
        alert('Failed to delete chat session. Please try again.');
      }
    }
  };

  const handleStartRename = (sessionId: string, currentTitle: string) => {
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const handleSaveRename = async (sessionId: string) => {
    if (!editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }

    try {
      await chatService.updateChatSessionTitle(sessionId, editingTitle.trim());
      // Update local state
      setChatSessions(prev => prev.map(s => 
        s.id === sessionId ? { ...s, title: editingTitle.trim() } : s
      ));
      // Call parent handler
      onRenameSession(sessionId, editingTitle.trim());
      setEditingSessionId(null);
    } catch (error) {
      console.error('Error renaming session:', error);
      alert('Failed to rename chat session. Please try again.');
    }
  };

  const handleCancelRename = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  if (collapsed) {
    return (
      <div className="h-full bg-slate-800 flex flex-col border-r border-slate-700">
        {/* Header - 468 Logo */}
        <div className="p-3 flex items-center justify-center">
          <div 
            className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center cursor-pointer" 
            onClick={onToggle}
          >
            <span className="text-white text-xs font-bold">468</span>
          </div>
        </div>

        {/* Navigation Icons */}
        <div className="flex-1 py-4">
          <div className="space-y-1 px-2">
            {sidebarItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={item.onClick}
                  className={`w-full h-12 p-0 rounded-xl transition-all flex items-center justify-center ${
                    item.active
                      ? "bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 text-blue-200 hover:bg-blue-500/30 hover:border-blue-400/50 shadow-lg shadow-blue-500/20"
                      : "text-white hover:text-blue-200 hover:bg-slate-700"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom Section - Just Account as round icon */}
        <div className="px-2 pb-2">
          <button 
            onClick={onNavigateToAccount}
            className="w-12 h-12 rounded-full bg-slate-700 hover:bg-slate-600 transition-all flex items-center justify-center mx-auto"
          >
            <User className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-800 flex flex-col border-r border-slate-700">
      {/* Header */}
      <div className="p-3 flex items-center justify-start">
        <div 
          className="w-6 h-6 bg-blue-600 rounded-sm flex items-center justify-center cursor-pointer" 
          onClick={onToggle}
        >
          <span className="text-white text-xs font-bold">⌘</span>
        </div>
      </div>

      {/* Search Bar - At the very top */}
      <div className="px-3 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-white/60" />
          <input
            type="text"
            placeholder="Search ⌘K"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-slate-900/50 border border-slate-700/50 rounded-md text-white text-xs placeholder-white/60 focus:outline-none focus:bg-slate-900 focus:border-slate-600 transition-all"
          />
        </div>
      </div>

      {/* Navigation Icons with Labels */}
      <div className="py-2">
        <div className="space-y-0.5 px-2">
          {sidebarItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={item.onClick}
                className={`w-full h-8 rounded-md transition-all flex items-center ${
                  item.active
                    ? "bg-slate-700 text-white"
                    : "text-white/80 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                <div className="flex items-center gap-2 px-2">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium">{item.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat Sessions Section - always show */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-3 pb-1 flex-shrink-0">
          <div className="flex items-center gap-2 text-white/60 text-xs">
            <span>Chats</span>
            {searchQuery && (
              <span className="text-white/40">({filteredSessions.length})</span>
            )}
          </div>
        </div>

          {/* Chat Sessions List - Properly constrained scrolling */}
          <div className="flex-1 overflow-y-auto px-2 sidebar-scroll" style={{ minHeight: 0, maxHeight: '100%' }}>
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
              </div>
            ) : filteredSessions.length > 0 ? (
              <div className="space-y-0.5">
                {filteredSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`group relative rounded-md transition-colors ${
                      activeSessionId === session.id
                        ? "bg-slate-700"
                        : "hover:bg-slate-700/50"
                    }`}
                  >
                    <div
                      onClick={() => onSessionSelect(session.id)}
                      className="cursor-pointer p-2 pr-12"
                    >
                      {editingSessionId === session.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveRename(session.id);
                              } else if (e.key === 'Escape') {
                                handleCancelRename();
                              }
                            }}
                            className="flex-1 bg-slate-600 border border-slate-500 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-400"
                            autoFocus
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveRename(session.id);
                            }}
                            className="p-1 text-green-400 hover:text-green-300"
                          >
                            <Check className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRename();
                            }}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className={`text-xs truncate ${
                          activeSessionId === session.id ? "text-white" : "text-white/80"
                        }`}>
                          {session.title}
                        </div>
                      )}
                    </div>
                    
                    {/* Action buttons - only show when not editing */}
                    {editingSessionId !== session.id && (
                      <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartRename(session.id, session.title);
                          }}
                          className="p-0.5 text-white/60 hover:text-blue-400 rounded"
                          title="Rename"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession(session.id, session.title);
                          }}
                          className="p-0.5 text-white/60 hover:text-red-400 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : searchQuery ? (
              <div className="p-4 text-center text-white/60 text-sm">
                No chats found for "{searchQuery}"
              </div>
            ) : (
              <div className="p-4 text-center text-white/60 text-sm">
                No chat sessions yet
              </div>
            )}
        </div>
      </div>

      {/* Bottom Section - Account as round icon */}
      <div className="p-4">
        <button 
          onClick={onNavigateToAccount}
          className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 transition-all flex items-center justify-center"
        >
          <User className="w-5 h-5 text-slate-300" />
        </button>
      </div>
    </div>
  );
} 